import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Seo } from "../components/onix/Seo";
import { SectionEyebrow } from "../components/onix/SectionEyebrow";
import { CapabilityList } from "../components/onix/CapabilityList";
import { DisclaimerBlock } from "../components/onix/DisclaimerBlock";
import { CTASection } from "../components/onix/CTASection";
import { Reveal } from "../components/onix/Reveal";
import { getTools, getTool, getProductDisclaimer } from "../data/tools";
import { useLang } from "../i18n/LanguageContext";

export default function Herramienta() {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const tc = t.common;
  const tools = getTools(lang);
  const tool = getTool(slug, lang);
  if (!tool) return <Navigate to="/404" replace />;

  const idx = tools.findIndex((x) => x.slug === slug);
  const prev = tools[(idx - 1 + tools.length) % tools.length];
  const next = tools[(idx + 1) % tools.length];

  return (
    <main className="pt-[76px]" key={slug}>
      <Seo
        title={`${tool.name} | ONIX`}
        description={tool.short}
        path={`/herramientas/${tool.slug}`}
      />

      <section className="onix-shell py-20 md:py-28 border-b border-onix-line">
        <Reveal>
          <nav className="flex items-center gap-3 mb-12 text-[11px] uppercase tracking-[0.12em] text-onix-gray" aria-label={t.tool.breadcrumbAria}>
            <Link data-testid="breadcrumb-ecosistema" to="/ecosistema" className="hover:text-onix-silver transition-colors">
              {t.tool.breadcrumb}
            </Link>
            <span aria-hidden="true">/</span>
            <span className="text-onix-silver">{tool.name}</span>
          </nav>
          <div className="flex flex-wrap items-center gap-5 mb-8">
            <span className="tech-label">{tool.num}</span>
          </div>
          <p className="eyebrow mb-6">{tool.name}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-serif leading-[1.05] text-onix-white max-w-4xl mb-8">
            {tool.title}
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-onix-silver max-w-2xl">{tool.description}</p>
        </Reveal>
      </section>

      <section className="onix-shell py-20 md:py-28">
        <Reveal>
          <SectionEyebrow index={tool.num} label={t.tool.capsSection} />
          <CapabilityList items={tool.capabilities} label={t.tool.capsLabel} />
        </Reveal>
        <Reveal delay={0.1}>
          <div className="grid lg:grid-cols-2 gap-6 mt-16">
            <DisclaimerBlock label={tc.limiteVisible}>{tool.limit}</DisclaimerBlock>
            <DisclaimerBlock label={tc.condiciones}>
              {getProductDisclaimer(lang)} {tc.condicionesExtra}
            </DisclaimerBlock>
          </div>
        </Reveal>
      </section>

      <section className="border-t border-onix-line">
        <div className="onix-shell py-10 flex items-center justify-between gap-6">
          <Link
            data-testid="tool-prev"
            to={`/herramientas/${prev.slug}`}
            className="inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.12em] text-onix-gray hover:text-onix-white transition-colors"
          >
            <ArrowLeft size={14} aria-hidden="true" />
            <span className="hidden sm:inline">{prev.name}</span>
            <span className="sm:hidden">{tc.anterior}</span>
          </Link>
          <span className="tech-label">
            {tool.num} / 07
          </span>
          <Link
            data-testid="tool-next"
            to={`/herramientas/${next.slug}`}
            className="inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.12em] text-onix-gray hover:text-onix-white transition-colors"
          >
            <span className="hidden sm:inline">{next.name}</span>
            <span className="sm:hidden">{tc.siguiente}</span>
            <ArrowRight size={14} aria-hidden="true" />
          </Link>
        </div>
      </section>

      <CTASection
        index="→"
        label={t.tool.ctaLabel}
        title={t.tool.ctaTitle}
        text={t.tool.ctaText}
        buttonLabel={tc.solicitarDemo}
        to="/contacto"
      />
    </main>
  );
}
